import {Spec} from 'immutability-helper';
import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {MediocreItemAction} from 'notebook/mediocre/EditorTypes';
import {
  getNoteLinkedGroupAdapters,
  isKnownNoteLinkedGroupAdapter,
} from 'notebook/notes/groups/NoteLinkedGroupAdapters';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPluginWithAdapter, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {FIELDS_PER_ROW} from 'notebook/structured-tables/StructuredTableForm';
import {getTableSkeletonHeight} from 'notebook/tables/core-ui/TableSkeleton';
import {OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {TABLE_HEADER_HEIGHT} from 'notebook/tables/TableConstants';
import {
  NoteLinkedObject,
  NoteLinkedObjectType,
  NotePart,
  NotePartWithItem,
} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';

import getNoteLinkedObjectsOfTypes from '../util/getNoteLinkedObjectsOfTypes';
import NotePartGrid from './note-part-grid/NotePartGrid';
import {NotePartGrid as NotePartGridType} from './note-part-grid/NotePartGridGroupConstants';
import {computeNotePartGridVirtualizedHeight} from './note-part-grid/util/computeVirtualizedHeight';
import {
  NoteLinkedGroup,
  NoteLinkedGroupItemProps,
  NoteLinkedGroupKind,
  UnknownNoteLinkedGroup,
  UnknownNoteLinkedGroupAdapter,
} from './NoteLinkedGroupConstants';
import {NoteLinkedGroupView} from './NoteLinkedGroupView';
import {isStructuredTableNotePart} from './util/assertIsStructuredTableNotePart';
import {getMetadataForRestoreForGroup} from './util/NoteLinkedGroupSelection';

const FORM_ROW_APPROX_HEIGHT = 90; // label + input

export const NOTE_LINKED_GROUP_PLUGIN: NoteEditorPluginWithAdapter<UnknownNoteLinkedGroupAdapter> = {
  type: NoteLinkedObjectType.NOTE_LINKED_GROUP,
  typeClass: 'is-group',
  title: 'Group',
  icon: 'notdef',

  get: (kind: NoteLinkedGroupKind): UnknownNoteLinkedGroupAdapter => {
    const adapter = getNoteLinkedGroupAdapters()[kind];
    assert(adapter != null, `Unknown note linked group kind: ${kind}`);
    return adapter;
  },

  component: {
    ...MediocrePlugins.selectablePluginBase,
    required: [],

    getVirtualizedHeight(item: NotePart & {item: NoteLinkedGroup<unknown>}) {
      const group = item.item;

      if (group.kind === NoteLinkedGroupKind.NOTE_PART_GRID) {
        return computeNotePartGridVirtualizedHeight(group as NotePartGridType);
      } else if (group.kind === NoteLinkedGroupKind.MIXTURE_GROUP) {
        let height = TABLE_HEADER_HEIGHT;
        const noteLinkedGroup = item.item;
        noteLinkedGroup.containedNoteParts.forEach((notePart) => {
          if (!isStructuredTableNotePart(notePart)) {
            assertAndContinue(false, 'Only structured tables supported in NoteLinkedGroup');
            return;
          }
          const adapter = getStructuredTableAdapters()[notePart.item.kind];
          const showAsForm = adapter.shouldShowAsForm(notePart.item);
          if (!showAsForm) {
            // NoteLinkedGroupView explicitly sets isCollapsible: false
            height += getTableSkeletonHeight(notePart.item.rows.length, false);
          } else {
            // Compute form height for form
            const formRows = Math.ceil(notePart.item.columns.length / FIELDS_PER_ROW);
            height += formRows * FORM_ROW_APPROX_HEIGHT;
          }
        });
        return height;
      }
      return 0;
    },

    getVirtualizationId(this: NoteEditor, notePart: NotePart & {item: NoteLinkedGroup<unknown>}): string {
      return notePart.item.provenanceUuid;
    },

    make(
      this: NoteEditor,
      notePart: NotePart & {item: NoteLinkedGroup<unknown>},
      index: number,
      editorProps: NoteEditorProps,
      itemProps: NoteLinkedGroupItemProps
    ): JSX.Element {
      const {item} = notePart;
      const {kind} = item;
      if (!isKnownNoteLinkedGroupAdapter(kind)) {
        throw new Error(`Unknown note linked group kind: ${kind}`);
      }
      if (kind === NoteLinkedGroupKind.NOTE_PART_GRID) {
        return (
          <NotePartGrid
            notePartCid={notePart.cid}
            group={item as NotePartGridType}
            editor={this}
            editorProps={editorProps}
            itemProps={itemProps}
          />
        );
      }
      return (
        <NoteLinkedGroupView
          notePartCid={notePart.cid}
          group={item}
          editor={this}
          editorProps={editorProps}
          {...itemProps}
        />
      );
    },

    getItemProps(
      this: NoteEditor,
      notePart: NotePartWithItem<UnknownNoteLinkedGroup>
    ): NoteLinkedGroupItemProps {
      const resolvedContainedItemIds =
        notePart.item.kind === NoteLinkedGroupKind.NOTE_PART_GRID
          ? notePart.item.containedNoteParts?.map((p) => {
              const cid = (p.item as NoteLinkedObject | null)?.cid;
              return cid ? this.props.getIdFromCid?.(cid) : undefined;
            })
          : undefined;

      // Include computed tables for the NLG's contained tables so that
      // MediocreItem's deep-equal check on itemProps detects when
      // evaluated values change (e.g. due to cross-group cell references).
      // Only relevant for groups that opt in to entry-level evaluation.
      let containedComputedTables: OutputTableMap | undefined;
      const adapter = isKnownNoteLinkedGroupAdapter(notePart.item.kind)
        ? getNoteLinkedGroupAdapters()[notePart.item.kind]
        : undefined;
      if (this.props.computedTables && adapter?.supportsEntryLevelEvaluation()) {
        containedComputedTables = {};
        for (const containedNotePart of notePart.item.containedNoteParts) {
          if (isStructuredTableNotePart(containedNotePart)) {
            const tableAdapter = getStructuredTableAdapters()[containedNotePart.item.kind];
            const tableName = tableAdapter.toTable(containedNotePart.item).name;
            if (this.props.computedTables[tableName]) {
              containedComputedTables[tableName] = this.props.computedTables[tableName];
            }
          }
        }
      }

      return {
        spreadsheetProps: {
          metadataForRestore: getMetadataForRestoreForGroup(notePart.item, this.props.metadataForRestore),
        },
        resolvedContainedItemIds,
        containedComputedTables,
        ...(adapter?.supportsEntryLevelEvaluation()
          ? {cellColors: this.props.cellColors, editingTable: this.props.editingTable}
          : {}),
      };
    },
  },

  getContent(item: NotePart & {item: UnknownNoteLinkedGroup}): unknown {
    return item.item;
  },

  itemFromHTML(
    this: NoteEditor,
    $el: JQuery,
    item: NotePart & {item: UnknownNoteLinkedGroup}
  ): NotePart | null {
    const adapter = getNoteLinkedGroupAdapters()[item.item.kind];
    const displayName = adapter.getDisplayName(item.item);
    if ($el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID) !== this.props.fileId) {
      notifications.warn(`Cannot paste "${displayName}" item between entries.`);
      return null;
    }
    if (
      !adapter.supportsCopyPaste() &&
      getNoteLinkedObjectsOfTypes(this.props.note, [NoteLinkedObjectType.NOTE_LINKED_GROUP]).some(
        (group) => (group as NoteLinkedGroup<unknown>).provenanceUuid === item.item.provenanceUuid
      )
    ) {
      notifications.warn(`Cannot create more than one copy of the same "${displayName}" item.`);
      return null;
    }
    return item;
  },

  itemToHTML($el: JQuery, item: NotePart): JQuery {
    // TODO(BNCH-14855): Put HTML on clipboard to support external copy and paste
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
    return $el;
  },

  setContent(props: {item: NoteLinkedGroup<unknown>}): Spec<NotePart> {
    return {
      item: {
        $apply(existingNoteLinkedObject: NoteLinkedObject): NoteLinkedObject {
          // eslint-disable-next-line benchlint/no-in-operator
          if ('item' in props && props.item != null) {
            return props.item;
          }
          return existingNoteLinkedObject;
        },
      },
    };
  },

  getReasonForDisallowedAction(
    this: NoteEditor,
    item: NotePart & {item: UnknownNoteLinkedGroup},
    action: MediocreItemAction
  ): string | null {
    if (action !== MediocreItemAction.DELETE) {
      return null;
    }
    const adapter = getNoteLinkedGroupAdapters()[item.item.kind];
    const allowDelete = adapter.allowDeleteGroup(item.item);
    return allowDelete.canDelete ? null : (allowDelete.reason ?? null);
  },
};
